import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Plus, Trash2, Calendar, Edit2, Video, 
    ChevronLeft, ChevronRight, Loader2, Save, X, Globe, Trophy,
    Filter, List, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { 
    fetchAllHighlights, createHighlight, updateHighlight, deleteHighlight, DBHighlight 
} from '../../../services/highlightService';
import { fetchLeagues, fetchGamesByLeagueAndDate, LeagueDB } from '../../../services/footballService';
import { getTeamName } from '../../../services/teamService';
import { getLeagueName } from '../../../services/leagueService';
import { Game, Language } from '../../../types';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { at } from '../../../i18nAdmin';

export const HighlightsTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [highlights, setHighlights] = useState<DBHighlight[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination & Filter States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [filterDate, setFilterDate] = useState('');
    const [filterLeague, setFilterLeague] = useState('');
    const [filterCountry, setFilterCountry] = useState('');

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isFormAnimating, setIsFormAnimating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Delete Modal State
    const [deleteModalState, setDeleteModalState] = useState<{ isOpen: boolean; id: string | null; isDeleting: boolean }>({
        isOpen: false,
        id: null,
        isDeleting: false
    });

    // Form State
    const [formData, setFormData] = useState({
        home_team: '',
        home_team_id: '',
        away_team: '',
        away_team_id: '',
        league_id: '',
        league_name: '',
        league_country: '',
        match_date: new Date().toISOString(),
        embed_id: '',
        match_id: ''
    });

    // Match Selection States
    const [availableLeagues, setAvailableLeagues] = useState<LeagueDB[]>([]);
    const [majorLeagueList, setMajorLeagueList] = useState<LeagueDB[]>([]);
    const [leagueSearchQuery, setLeagueSearchQuery] = useState('');
    const [selectedLeagueId, setSelectedLeagueId] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date(new Date().getTime() + 9 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [availableGames, setAvailableGames] = useState<Game[]>([]);
    const [isGamesLoading, setIsGamesLoading] = useState(false);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const data = await fetchAllHighlights();
            setHighlights(data);
        } catch (e) {
            showToast('error', 'Failed to load highlights');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        fetchLeagues(true).then(setAvailableLeagues);
        fetchLeagues(false).then(setMajorLeagueList);
    }, []);

    const filteredAvailableLeagues = useMemo(() => {
        const query = leagueSearchQuery.trim().toLowerCase();
        if (!query) return availableLeagues;
        
        // Exact ID match should come first
        const idMatch = availableLeagues.find(l => l.id.toString() === query);
        
        const others = availableLeagues.filter(l => 
            l.id.toString() !== query && (
                l.name.toLowerCase().includes(query) || 
                l.id.toString().includes(query)
            )
        );

        return idMatch ? [idMatch, ...others] : others;
    }, [availableLeagues, leagueSearchQuery]);

    const fetchGames = async () => {
        if (!selectedLeagueId || !selectedDate) {
            showToast('info', at(lang, 'selectLeagueDate'));
            return;
        }
        try {
            setIsGamesLoading(true);
            const games = await fetchGamesByLeagueAndDate(selectedLeagueId, selectedDate);
            if (!games || games.length === 0) {
                showToast('info', at(lang, 'noGamesFound'));
                setAvailableGames([]);
            } else {
                setAvailableGames(games);
                showToast('success', at(lang, 'foundGames').replace('{{count}}', games.length.toString()));
            }
        } catch (e) {
            showToast('error', 'Failed to fetch games');
        } finally {
            setIsGamesLoading(false);
        }
    };

    const handleSelectGame = (game: Game) => {
        // Find league info to get country
        const leagueInfo = availableLeagues.find(l => l.id.toString() === game.league.toString());
        
        setFormData({
            ...formData,
            home_team: game.homeTeam.name,
            home_team_id: game.homeTeam.id?.toString() || '',
            away_team: game.awayTeam.name,
            away_team_id: game.awayTeam.id?.toString() || '',
            league_id: game.league,
            league_name: game.leagueName || '',
            league_country: leagueInfo?.country || '',
            match_date: new Date(new Date(game.dateLabel + 'T' + game.time).getTime() + 9 * 60 * 60 * 1000).toISOString(),
            match_id: game.id
        });
        showToast('success', at(lang, 'gameInfoApplied'));
    };

    // Extract unique values for filters
    const leagues = useMemo(() => Array.from(new Set(highlights.map(h => h.league_name).filter(Boolean))), [highlights]);
    const countries = useMemo(() => Array.from(new Set(highlights.map(h => h.league_country).filter(Boolean))), [highlights]);

    // Filtering Logic
    const filteredHighlights = useMemo(() => {
        return highlights.filter(h => {
            const matchesSearch = h.home_team.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 h.away_team.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 (h.league_name || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesDate = filterDate ? h.match_date.startsWith(filterDate) : true;
            const matchesLeague = filterLeague ? h.league_name === filterLeague : true;
            const matchesCountry = filterCountry ? h.league_country === filterCountry : true;
            
            return matchesSearch && matchesDate && matchesLeague && matchesCountry;
        });
    }, [highlights, searchTerm, filterDate, filterLeague, filterCountry]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredHighlights.length / pageSize);
    const paginatedHighlights = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredHighlights.slice(start, start + pageSize);
    }, [filteredHighlights, currentPage, pageSize]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterDate, filterLeague, filterCountry, pageSize]);

    const handleOpenCreate = () => {
        setEditingId(null);
        const kstNow = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
        setFormData({
            home_team: '',
            home_team_id: '',
            away_team: '',
            away_team_id: '',
            league_id: '',
            league_name: '',
            league_country: '',
            match_date: kstNow.toISOString(),
            embed_id: '',
            match_id: ''
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const handleOpenEdit = (h: DBHighlight) => {
        setEditingId(h.id);
        setFormData({
            home_team: h.home_team,
            home_team_id: h.home_team_id || '',
            away_team: h.away_team,
            away_team_id: h.away_team_id || '',
            league_id: h.league_id || '',
            league_name: h.league_name || '',
            league_country: h.league_country || '',
            match_date: h.match_date,
            embed_id: h.embed_id,
            match_id: h.match_id || ''
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const closeForm = () => {
        setIsFormAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            setEditingId(null);
        }, 500);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (editingId) {
                await updateHighlight(editingId, formData as any);
                showToast('success', at(lang, 'highlightUpdated'));
            } else {
                await createHighlight(formData as any);
                showToast('success', at(lang, 'highlightCreated'));
            }
            closeForm();
            loadData();
        } catch (e: any) {
            showToast('error', e.message || 'Operation failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (id: string) => {
        setDeleteModalState({ isOpen: true, id, isDeleting: false });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModalState.id) return;
        try {
            setDeleteModalState(prev => ({ ...prev, isDeleting: true }));
            await deleteHighlight(deleteModalState.id);
            showToast('success', at(lang, 'deletedSuccess'));
            loadData();
        } catch (e) {
            showToast('error', 'Delete failed');
        } finally {
            setDeleteModalState({ isOpen: false, id: null, isDeleting: false });
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
                        {at(lang, 'highlightsManage')}
                    </h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        {at(lang, 'highlightsDesc')}
                    </p>
                </div>
                <button 
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
                >
                    <Plus size={16} strokeWidth={3} />
                    {at(lang, 'addNew')}
                </button>
            </div>

            {/* Filters Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input 
                        type="text" 
                        placeholder={at(lang, 'search')} 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-rose-500" size={16} />
                    <input 
                        type="date"
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm cursor-pointer"
                        value={filterDate}
                        onChange={e => setFilterDate(e.target.value)}
                    />
                </div>

                <div className="relative">
                    <Trophy className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-500" size={16} />
                    <select 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm cursor-pointer appearance-none"
                        value={filterLeague}
                        onChange={e => setFilterLeague(e.target.value)}
                    >
                        <option value="">{at(lang, 'allLeagues')}</option>
                        {leagues.map(l => <option key={l} value={l!}>{l}</option>)}
                    </select>
                </div>

                <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-500" size={16} />
                    <select 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm cursor-pointer appearance-none"
                        value={filterCountry}
                        onChange={e => setFilterCountry(e.target.value)}
                    >
                        <option value="">{at(lang, 'allCountries')}</option>
                        {countries.map(c => <option key={c} value={c!}>{c}</option>)}
                    </select>
                </div>

                <div className="relative">
                    <List className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <select 
                        className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm cursor-pointer appearance-none"
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        <option value={30}>30 {at(lang, 'itemsPerPage')}</option>
                        <option value={50}>50 {at(lang, 'itemsPerPage')}</option>
                        <option value={100}>100 {at(lang, 'itemsPerPage')}</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[300px]">{at(lang, 'matchInfo')}</th>
                                <th className="px-6 py-5 min-w-[120px]">{at(lang, 'embedId')}</th>
                                <th className="px-6 py-5 min-w-[120px]">{at(lang, 'matchId')}</th>
                                <th className="px-6 py-5 min-w-[150px]">{at(lang, 'date')}</th>
                                <th className="px-8 py-5 text-right min-w-[120px]">{at(lang, 'ops')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center">
                                        <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={32} />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Loading Highlights...</p>
                                    </td>
                                </tr>
                            ) : paginatedHighlights.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center opacity-30">
                                        <Video size={48} className="mx-auto mb-4" />
                                        <p className="text-sm font-black uppercase tracking-widest">No Highlights Found</p>
                                    </td>
                                </tr>
                            ) : (
                                paginatedHighlights.map((h) => (
                                    <tr key={h.id} className="md:hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 aspect-video rounded-lg overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                                                    <img 
                                                        src={`https://img.youtube.com/vi/${h.embed_id}/mqdefault.jpg`} 
                                                        className="w-full h-full object-cover"
                                                        alt="thumbnail"
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-[10px] font-bold text-rose-500 uppercase tracking-widest leading-none">
                                                            {h.league_country || 'UN'}: {h.league_id ? getLeagueName(Number(h.league_id), lang, h.league_name) : h.league_name || '-'}
                                                        </span>
                                                        <p className="text-sm font-black text-slate-900 truncate uppercase tracking-tight">
                                                            {getTeamName(h.home_team_id, lang, h.home_team)} vs {getTeamName(h.away_team_id, lang, h.away_team)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 font-mono text-[11px] text-slate-500">
                                            {h.embed_id}
                                        </td>
                                        <td className="px-6 py-6 font-mono text-[11px] text-indigo-500 font-bold">
                                            {h.match_id || '-'}
                                        </td>
                                        <td className="px-6 py-6 font-bold text-slate-400 text-[11px] tabular-nums tracking-tighter">
                                            {new Date(h.match_date).toLocaleDateString()}
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenEdit(h)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Edit2 size={16}/>
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteClick(h.id)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Page {currentPage} of {totalPages || 1} ({filteredHighlights.length} Items Total)
                    </p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={totalPages === 0 || currentPage === totalPages}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {isFormOpen && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !m-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeForm}></div>
                    <div className={`relative bg-white w-full max-w-xl rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                {editingId ? at(lang, 'editHighlight') : at(lang, 'newHighlight')}
                            </h3>
                            <button onClick={closeForm} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>

                        <div className="px-8 pt-6 pb-2 bg-slate-50 border-b border-slate-100">
                            <label className="text-[10px] font-black text-rose-500 uppercase tracking-widest block mb-4">{at(lang, 'quickGameSelection')}</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div className="space-y-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                                        <input 
                                            type="text"
                                            placeholder={at(lang, 'searchLeague')}
                                            className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-9 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all"
                                            value={leagueSearchQuery}
                                            onChange={e => setLeagueSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <select 
                                        className="w-full bg-white border border-slate-200 rounded-xl py-2 px-4 text-xs font-bold text-slate-900 outline-none"
                                        value={selectedLeagueId}
                                        onChange={e => setSelectedLeagueId(e.target.value)}
                                    >
                                        <option value="">{at(lang, 'selectLeague')}</option>
                                        
                                        {!leagueSearchQuery && (
                                            <optgroup label={at(lang, 'majorLeagues')}>
                                                {majorLeagueList.map(l => (
                                                    <option key={`major-${l.id}`} value={l.id}>{getLeagueName(Number(l.id), lang, l.name)}</option>
                                                ))}
                                            </optgroup>
                                        )}

                                        <optgroup label={leagueSearchQuery ? at(lang, 'searchResults') : at(lang, 'allLeagues')}>
                                            {filteredAvailableLeagues.map(l => (
                                                <option key={l.id} value={l.id}>{getLeagueName(Number(l.id), lang, l.name)}</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                        <input 
                                            type="date"
                                            className="flex-1 bg-white border border-slate-200 rounded-xl py-2 px-4 text-xs font-bold text-slate-900 outline-none"
                                            value={selectedDate}
                                            onChange={e => setSelectedDate(e.target.value)}
                                        />
                                        <button 
                                            type="button"
                                            onClick={fetchGames}
                                            disabled={isGamesLoading || !selectedLeagueId}
                                            className="p-2 bg-slate-900 text-white rounded-xl md:hover:bg-black transition-all disabled:opacity-30"
                                        >
                                            <RefreshCw size={16} className={isGamesLoading ? 'animate-spin' : ''} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {availableGames.length > 0 && (
                                <div className="max-h-32 overflow-y-auto space-y-1 mb-4 custom-scrollbar">
                                    {availableGames.map(g => (
                                        <button
                                            key={g.id}
                                            type="button"
                                            onClick={() => handleSelectGame(g)}
                                            className="w-full text-left px-4 py-2 bg-white border border-slate-100 rounded-lg md:hover:border-rose-500 transition-all group"
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <img src={g.homeTeam.logo} alt="" className="w-4 h-4 object-contain" />
                                                        <span className="text-[10px] font-bold text-slate-700 uppercase truncate max-w-[80px] md:max-w-none md:group-hover:text-rose-500">
                                                            {getTeamName(g.homeTeam.id, lang, g.homeTeam.name)}
                                                        </span>
                                                    </div>
                                                    <span className="text-[9px] font-black text-slate-300 uppercase shrink-0">vs</span>
                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        <span className="text-[10px] font-bold text-slate-700 uppercase truncate max-w-[80px] md:max-w-none md:group-hover:text-rose-500">
                                                            {getTeamName(g.awayTeam.id, lang, g.awayTeam.name)}
                                                        </span>
                                                        <img src={g.awayTeam.logo} alt="" className="w-4 h-4 object-contain" />
                                                    </div>
                                                </div>
                                                <span className="text-[9px] font-black text-slate-300 tabular-nums uppercase shrink-0">
                                                    {g.time}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSave} className="p-8 space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2 hidden">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">League ID</label>
                                    <input 
                                        placeholder="Optional"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.league_id}
                                        onChange={e => setFormData({...formData, league_id: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2 md:col-span-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Home Team</label>
                                    <input 
                                        required
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.home_team}
                                        onChange={e => setFormData({...formData, home_team: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2 hidden">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Home Team ID</label>
                                    <input 
                                        placeholder="Optional"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.home_team_id}
                                        onChange={e => setFormData({...formData, home_team_id: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2 md:col-span-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Away Team</label>
                                    <input 
                                        required
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.away_team}
                                        onChange={e => setFormData({...formData, away_team: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2 hidden">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Away Team ID</label>
                                    <input 
                                        placeholder="Optional"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.away_team_id}
                                        onChange={e => setFormData({...formData, away_team_id: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2 md:col-span-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">YouTube Embed ID</label>
                                    <input 
                                        required
                                        placeholder="e.g. dQw4w9WgXcQ"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.embed_id}
                                        onChange={e => setFormData({...formData, embed_id: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2 hidden">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Game ID</label>
                                    <input 
                                        placeholder="Optional"
                                        className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                        value={formData.match_id}
                                        onChange={e => setFormData({...formData, match_id: e.target.value})}
                                    />
                                </div>
                            </div>

                            <button 
                                type="submit"
                                disabled={isSaving}
                                className="w-full py-4 bg-slate-900 text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-200 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {at(lang, 'saveHighlight')}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, id: null, isDeleting: false })}
                onConfirm={handleConfirmDelete}
                isLoading={deleteModalState.isDeleting}
                title={at(lang, 'deleteConfirm')}
                description={at(lang, 'deleteConfirmDesc')}
                confirmLabel={at(lang, 'delete')}
                cancelLabel={at(lang, 'cancel')}
            />
        </div>
    );
};
